import { useState } from "react";
import { BookOpen, X, Sparkles, Lock, Unlock } from "lucide-react";

/* -----------------------------------------------------------
   CarnetVirtuel — Bouton flottant + drawer bas
   Mémorise les découvertes : codes ouverts, indices utilisés,
   anecdotes des énigmes résolues.
   ----------------------------------------------------------- */

export default function CarnetVirtuel({ etapes, cadenasOuverts, codesOuverts = [] }) {
  const [open, setOpen] = useState(false);

  // Map { numeroCadenas → { userPhotoB64 } }
  const souvenirsMap = new Map();
  codesOuverts.forEach((c) => {
    if (c.userPhotoB64) souvenirsMap.set(c.numeroCadenas, c.userPhotoB64);
  });

  // Aplati toutes les énigmes avec leur état
  const entries = [];
  etapes?.forEach((et) => {
    et.enigmes?.forEach((en) => {
      const ouvert = cadenasOuverts.has(en.numeroCadenas);
      entries.push({
        etapeOrdre: et.ordre,
        etapeNom: et.nom,
        etapeEmoji: et.emoji || "📍",
        numeroCadenas: en.numeroCadenas,
        codeCadenas: en.codeCadenas,
        resumeIdee: en.resumeIdee,
        indice1: en.indice1,
        explication: en.explication,
        type: (en.type || "TEXTE").toUpperCase(),
        archivePhotoUrl: en.archivePhotoUrl,
        archiveAnnee: en.archiveAnnee,
        userPhoto: souvenirsMap.get(en.numeroCadenas),
        ouvert,
      });
    });
  });

  const nbOuverts = entries.filter((e) => e.ouvert).length;

  return (
    <>
      {/* Bouton flottant */}
      <button
        onClick={() => setOpen(true)}
        data-testid="carnet-toggle"
        aria-label="Ouvrir le carnet"
        className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-3 bg-or hover:bg-or-100 text-noir font-medium rounded-sm shadow-[0_10px_40px_-10px_rgba(200,146,42,0.7)] transition-all"
      >
        <BookOpen className="w-4 h-4" strokeWidth={2} />
        <span className="text-sm font-mono uppercase tracking-widest">Carnet</span>
        {nbOuverts > 0 && (
          <span className="font-mono text-xs bg-noir text-or px-2 py-0.5 rounded-sm">
            {nbOuverts}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-end"
          data-testid="carnet-drawer"
        >
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-noir/80 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* Panel */}
          <div className="relative w-full sm:max-w-md bg-noir-100 border-l border-or/25 h-full overflow-y-auto flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-noir-100/95 backdrop-blur border-b border-or/15 p-5 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-sm bg-or/10 border border-or/40 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-or" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80">
                    Le grimoire
                  </div>
                  <h2 className="font-display text-lg text-parchemin leading-tight">
                    Carnet de bord
                  </h2>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="carnet-close"
                aria-label="Fermer le carnet"
                className="p-2 text-parchemin/50 hover:text-parchemin transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Progression */}
            <div className="p-5 border-b border-or/10 bg-vert-900/20">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-vert-50/80">
                  Progression
                </span>
                <span className="font-display text-xl text-parchemin stat-graved">
                  {nbOuverts}
                  <span className="text-parchemin/40">/{entries.length}</span>
                </span>
              </div>
              <div className="h-1.5 bg-noir-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-vert-50 to-or transition-all duration-700"
                  style={{
                    width: `${
                      entries.length ? (nbOuverts / entries.length) * 100 : 0
                    }%`,
                  }}
                />
              </div>
            </div>

            {/* Contenu */}
            <div className="flex-1 p-5 space-y-4">
              {entries.length === 0 ? (
                <p className="text-parchemin/50 text-sm italic font-display text-center py-8">
                  Aucune énigme dans ce parcours.
                </p>
              ) : (
                entries.map((e) => (
                  <div
                    key={e.numeroCadenas}
                    className={`border rounded-sm p-4 ${
                      e.ouvert
                        ? "bg-vert-900/30 border-vert-50/40"
                        : "bg-noir-50/40 border-or/15"
                    }`}
                    data-testid={`carnet-entry-${e.numeroCadenas}`}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <span className="text-xl leading-none">{e.etapeEmoji}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70">
                          Étape {e.etapeOrdre} · Cadenas #{e.numeroCadenas}
                        </div>
                        <div className="text-sm text-parchemin font-medium mt-0.5">
                          {e.resumeIdee || e.etapeNom}
                        </div>
                      </div>
                      {e.ouvert ? (
                        <Unlock className="w-4 h-4 text-vert-50 flex-shrink-0" strokeWidth={1.5} />
                      ) : (
                        <Lock className="w-4 h-4 text-parchemin/30 flex-shrink-0" strokeWidth={1.5} />
                      )}
                    </div>

                    {e.ouvert ? (
                      <>
                        {/* Souvenir photo (VOYAGE_TEMPS) : avant/après */}
                        {e.type === "VOYAGE_TEMPS" && (e.archivePhotoUrl || e.userPhoto) && (
                          <div className="mt-3 grid grid-cols-2 gap-1.5">
                            {e.archivePhotoUrl && (
                              <figure className="border border-or/30 rounded-sm overflow-hidden">
                                <img
                                  src={e.archivePhotoUrl}
                                  alt="Archive"
                                  className="w-full aspect-square object-cover"
                                  style={{ filter: "sepia(0.5) contrast(1.1)" }}
                                />
                                <figcaption className="p-1 text-center bg-noir-100 font-mono text-[8px] tracking-widest uppercase text-or/70">
                                  Hier
                                </figcaption>
                              </figure>
                            )}
                            {e.userPhoto && (
                              <figure className="border border-vert-50/50 rounded-sm overflow-hidden">
                                <img
                                  src={e.userPhoto}
                                  alt="Souvenir"
                                  className="w-full aspect-square object-cover"
                                />
                                <figcaption className="p-1 text-center bg-vert-900/60 font-mono text-[8px] tracking-widest uppercase text-vert-50">
                                  Aujourd&apos;hui
                                </figcaption>
                              </figure>
                            )}
                          </div>
                        )}

                        <div className="mt-3 p-2.5 bg-noir border border-or/30 rounded-sm text-center">
                          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-or/60 mb-1">
                            Code
                          </div>
                          <div className="font-mono font-bold text-xl text-or tracking-[0.3em] stat-graved">
                            {e.codeCadenas}
                          </div>
                        </div>
                        {e.explication && (
                          <div className="mt-3 flex items-start gap-2 text-xs text-parchemin/75 italic font-display leading-relaxed">
                            <Sparkles className="w-3 h-3 text-vert-50 flex-shrink-0 mt-0.5" />
                            <span>{e.explication}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Preview archive pour VOYAGE_TEMPS non résolu */}
                        {e.type === "VOYAGE_TEMPS" && e.archivePhotoUrl && (
                          <figure className="mt-2 border border-or/20 rounded-sm overflow-hidden opacity-60">
                            <img
                              src={e.archivePhotoUrl}
                              alt="Archive à retrouver"
                              className="w-full aspect-video object-cover"
                              style={{ filter: "sepia(0.6) contrast(1.1)" }}
                            />
                            <figcaption className="p-1 text-center bg-noir-100 font-mono text-[9px] tracking-widest uppercase text-or/70">
                              À retrouver · {e.archiveAnnee || "archive"}
                            </figcaption>
                          </figure>
                        )}
                        <p className="text-xs text-parchemin/45 italic font-display leading-relaxed mt-2">
                          {e.indice1 || "Résolvez l'énigme pour révéler les détails."}
                        </p>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
